import { useParts } from '@/core/domain/operational/Parts/Parts.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Button, Modal } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { Part } from '@/core/domain/operational/Parts/Parts.types';
import { useIntl } from 'react-intl';
import LoadMoreSummary from '@/components/_common/LoadMoreSummary';
import { useState } from 'react';
import { ListSuppliersQuery } from '@/core/domain/people/Suppliers/Suppliers.types';
import SelectPartReplacementFilters from './SelectPartReplacementFilters';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { InfiniteTable } from '@/components/_common';
import { useRouteMatch } from 'react-router-dom';
import { useDebounce } from '@/hooks';

interface Props {
  isModalVisible: boolean;
  setIsModalVisible: () => void;
  handlerOnOkay?: () => void;
  handlerPartSelect?: (part: Part) => void;
  removeFooter?: boolean;
}

const SelectPartReplacementModal = ({
  isModalVisible,
  setIsModalVisible,
  handlerOnOkay,
  handlerPartSelect,
  removeFooter,
}: Props) => {
  const { partId } = useRouteMatch<{ partId: string }>().params;
  const intl = useIntl().formatMessage;

  const [queryParams, setQueryParams] = useState<ListSuppliersQuery>({
    order: 'ASC',
  });
  const queryDebounce = useDebounce(queryParams);

  const partsList = useParts(queryDebounce, isModalVisible);
  const parts = flatInfiniteData(partsList.data);

  const columns: ColumnsType<Part> = [
    {
      key: 'name',
      dataIndex: 'name',
      title: intl({ id: 'parts.partsReplacement.modal.description' }),
      ellipsis: true,
    },
    {
      key: 'manufacturingCode',
      dataIndex: 'manufacturingCode',
      title: intl({ id: 'parts.partsReplacement.modal.manufacturingCode' }),
      ellipsis: true,
    },
    {
      key: 'barCode',
      dataIndex: 'barCode',
      title: intl({ id: 'parts.partsReplacement.modal.barCode' }),
      ellipsis: true,
    },
    {
      key: 'sku',
      dataIndex: 'sku',
      title: intl({ id: 'parts.partsReplacement.modal.internalCode' }),
      ellipsis: true,
    },
    {
      key: 'companyCode',
      dataIndex: 'companyCode',
      title: intl({ id: 'parts.partsReplacement.modal.companyCode' }),
      ellipsis: true,
    },
    handlerPartSelect
      ? {
          key: 'action',
          dataIndex: 'action',
          width: COLUMNS_WIDTH.default,
          align: 'right',
          render: (_, item) => (
            <Button
              type="primary"
              onClick={() => handlerPartSelect(item)}
              icon={<CheckOutlined />}
            />
          ),
        }
      : {},
  ];

  return (
    <Modal
      title={intl({ id: 'parts.partsReplacement.modal.title' })}
      open={isModalVisible}
      onOk={() => {
        if (handlerOnOkay) {
          handlerOnOkay();
        }
      }}
      onCancel={() => {
        setQueryParams({ order: 'ASC' });
        setIsModalVisible();
      }}
      width="90%"
      destroyOnClose
      footer={removeFooter ? false : undefined}
    >
      <SelectPartReplacementFilters setQueryParams={setQueryParams} />

      <InfiniteTable
        dataSource={parts.filter((part) => part.id !== +partId)}
        scroll={{ x: getTableScrollX(columns) }}
        columns={columns}
        pagination={false}
        loading={partsList.isFetching}
        summary={() =>
          partsList.hasNextPage ? (
            <LoadMoreSummary
              onClick={partsList.fetchNextPage}
              columnsCount={columns.length}
            />
          ) : null
        }
      />
    </Modal>
  );
};

export default SelectPartReplacementModal;
